import { useCallback, useEffect, useMemo, useState } from "react";
import { humanizeApiError } from "../api/client";
import {
  ANALYTICS_THRESHOLDS_DEFAULTS,
  type AnalyticsThresholds,
  CTA_KEY,
  type CtaKind,
  type ExtensionSelectors,
  PRIVACY_URL_KEY,
  SELECTORS_KEY,
  type SettingValue,
  THRESHOLDS_KEY,
  type ViewerCta,
  settingsApi,
} from "../api/settings";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

type Status =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

export function SettingsPage() {
  const [status, setStatus] = useState<Status>({ kind: "loading" });
  const [effective, setEffective] = useState<ExtensionSelectors | null>(null);
  const [override, setOverride] = useState<SettingValue | undefined>(undefined);
  const [draft, setDraft] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Пороги аналитики (PRD §7.4) — отдельная карточка, общий PATCH /settings.
  const [thresholdsStored, setThresholdsStored] = useState<AnalyticsThresholds>(
    ANALYTICS_THRESHOLDS_DEFAULTS,
  );

  // Плавающая CTA в viewer (PRD §6.2) — отдельная карточка, общий PATCH /settings.
  // null = кнопка отключена (запись в БД хранится как null).
  const [ctaStored, setCtaStored] = useState<ViewerCta | null>(null);

  // Privacy URL (PRD §13.3). null = дефолт `/privacy` (статика Caddy).
  const [privacyUrlStored, setPrivacyUrlStored] = useState<string | null>(null);

  const load = useCallback(async () => {
    setStatus({ kind: "loading" });
    try {
      const [all, eff] = await Promise.all([
        settingsApi.get(),
        settingsApi.getEffectiveSelectors(),
      ]);
      const current = all.settings[SELECTORS_KEY];
      setEffective(eff);
      setOverride(current);
      setDraft(current !== undefined ? JSON.stringify(current, null, 2) : "");
      const storedThresholds = all.settings[THRESHOLDS_KEY];
      setThresholdsStored(parseThresholds(storedThresholds));
      const storedCta = all.settings[CTA_KEY];
      setCtaStored(isViewerCta(storedCta) ? storedCta : null);
      const storedPrivacyUrl = all.settings[PRIVACY_URL_KEY];
      setPrivacyUrlStored(
        typeof storedPrivacyUrl === "string" && storedPrivacyUrl.length > 0
          ? storedPrivacyUrl
          : null,
      );
      setStatus({ kind: "ready" });
    } catch (err) {
      setStatus({ kind: "error", message: extractMessage(err) });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const parseResult = useMemo(() => parseJson(draft), [draft]);
  const isDirty = useMemo(() => {
    const original = override !== undefined ? JSON.stringify(override, null, 2) : "";
    return draft !== original;
  }, [draft, override]);

  async function onSave() {
    if (parseResult.kind !== "ok") return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await settingsApi.patch({
        updates: { [SELECTORS_KEY]: parseResult.value },
      });
      const stored = res.settings[SELECTORS_KEY];
      setOverride(stored);
      setDraft(stored !== undefined ? JSON.stringify(stored, null, 2) : "");
      setSavedAt(new Date());
      // После сохранения подтянем эффективный конфиг — он мог измениться.
      try {
        setEffective(await settingsApi.getEffectiveSelectors());
      } catch {
        /* effective можно не пересчитывать, если запрос сорвался */
      }
    } catch (err) {
      setSaveError(extractMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function onPrefillFromEffective() {
    if (!effective) return;
    setDraft(JSON.stringify(effective, null, 2));
  }

  function onRevert() {
    setDraft(override !== undefined ? JSON.stringify(override, null, 2) : "");
    setSaveError(null);
  }

  if (status.kind === "loading") {
    return <p className="text-sm text-muted-foreground">Загрузка…</p>;
  }
  if (status.kind === "error") {
    return (
      <div className="space-y-3 max-w-3xl">
        <p className="text-sm text-destructive">{status.message}</p>
        <Button variant="outline" onClick={() => void load()}>
          Повторить
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 max-w-5xl">
      <SettingsNav />
      <div className="space-y-6 flex-1 min-w-0">
        <section id="section-analytics" className="scroll-mt-24">
          <ThresholdsCard
            stored={thresholdsStored}
            onSaved={setThresholdsStored}
          />
        </section>

        <section id="section-viewer" className="scroll-mt-24 space-y-6">
          <CtaCard stored={ctaStored} onSaved={setCtaStored} />
          <PrivacyUrlCard
            stored={privacyUrlStored}
            onSaved={setPrivacyUrlStored}
          />
        </section>

        <section id="section-extension" className="scroll-mt-24 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Селекторы расширения</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>
                <strong className="text-foreground">
                  Технический раздел — только для администратора-разработчика.
                </strong>{" "}
                Менеджеру по продажам сюда заходить не нужно.
              </p>
              <p>
                Браузерное расширение распознаёт окно нового письма в Gmail,
                Outlook Web и Яндекс.Почте по разметке этих сайтов. Когда
                почтовый сервис меняет вёрстку и расширение перестаёт находить
                нужные кнопки — селекторы можно поправить здесь, без
                переустановки расширения у сотрудников. Правки доходят до всех
                в течение часа.
              </p>
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                <strong>Осторожно:</strong> неаккуратная правка ломает
                расширение у всех сотрудников одновременно — изменения сразу
                попадают на прод. Не редактируйте, если не уверены.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Текущий конфиг (для просмотра)</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Длинный JSON-блок по умолчанию свёрнут: на странице важнее
                * редактируемые «Переопределения». Если admin-разработчик
                * захочет посмотреть, что реально отдаётся расширению, он
                * раскроет блок одним кликом. */}
              <details className="group" data-testid="effective-config-details">
                <summary className="cursor-pointer text-sm text-foreground/80 hover:text-foreground list-none flex items-center gap-2">
                  <span aria-hidden="true" className="transition-transform group-open:rotate-90">▸</span>
                  Показать JSON
                </summary>
                <div className="mt-3">
                  <p className="text-sm text-muted-foreground mb-2">
                    Что реально получит расширение по{" "}
                    <code className="font-mono">GET /extension/config</code> (дефолты ⊕
                    переопределения).
                  </p>
                  <pre className="text-xs font-mono bg-muted rounded-md p-3 max-h-80 overflow-auto">
                    {effective ? JSON.stringify(effective, null, 2) : "—"}
                  </pre>
                  <div className="mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onPrefillFromEffective}
                      disabled={!effective}
                    >
                      Скопировать как шаблон
                    </Button>
                  </div>
                </div>
              </details>
            </CardContent>
          </Card>

          <Card data-testid="selectors-override-card">
            <CardHeader>
              <CardTitle>Переопределения селекторов</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                JSON хранится в БД (ключ{" "}
                <code className="font-mono">{SELECTORS_KEY}</code>) и
                накладывается поверх встроенных дефолтов на уровне отдельных
                почтовых клиентов. Пустое поле — переопределений нет,
                расширение работает по дефолтам из релизной сборки.
              </p>

              <div className="space-y-2">
                <Label htmlFor="selectors-json">JSON</Label>
                <textarea
                  id="selectors-json"
                  className="w-full min-h-[320px] rounded-md border border-input bg-background p-3 text-xs font-mono"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  spellCheck={false}
                  aria-invalid={parseResult.kind === "error"}
                  aria-describedby="selectors-json-status"
                />
                <p
                  id="selectors-json-status"
                  className={
                    parseResult.kind === "error"
                      ? "text-xs text-destructive"
                      : "text-xs text-muted-foreground"
                  }
                >
                  {parseResult.kind === "error"
                    ? `Ошибка JSON: ${parseResult.message}`
                    : draft.trim() === ""
                    ? "Пусто — переопределения не отправляются (будут использоваться только дефолты)."
                    : "JSON валиден локально. Финальную валидацию структуры выполняет сервер."}
                </p>
              </div>

              {saveError && <p className="text-sm text-destructive">{saveError}</p>}

              <div className="flex items-center gap-2">
                <Button
                  onClick={() => void onSave()}
                  disabled={
                    saving ||
                    parseResult.kind === "error" ||
                    draft.trim() === "" ||
                    !isDirty
                  }
                >
                  {saving ? "Загрузка…" : "Сохранить"}
                </Button>
                <Button
                  variant="outline"
                  onClick={onRevert}
                  disabled={saving || !isDirty}
                >
                  Отменить
                </Button>
                {savedAt && !isDirty && !saveError && (
                  <span className="text-xs text-muted-foreground">
                    Сохранено в {savedAt.toLocaleTimeString("ru-RU")}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}

const SECTION_ITEMS: { id: string; label: string }[] = [
  { id: "section-analytics", label: "Аналитика" },
  { id: "section-viewer", label: "Страница с КП" },
  { id: "section-extension", label: "Браузерное расширение" },
];

function SettingsNav() {
  // Активный пункт sidebar отслеживаем по IntersectionObserver: подсвечиваем
  // тот раздел, чей якорь сейчас находится в верхней трети вьюпорта. Если
  // ни один не пересекается (например, страница перепрыгнула выше первого
  // якоря) — оставляем последнее значение.
  const [active, setActive] = useState<string>(SECTION_ITEMS[0]!.id);

  useEffect(() => {
    // jsdom (vitest) не реализует IntersectionObserver — пропускаем
    // подсветку, сайдбар остаётся со стартовым активным пунктом.
    if (typeof IntersectionObserver === "undefined") return;
    const observed: HTMLElement[] = SECTION_ITEMS.map((s) =>
      document.getElementById(s.id),
    ).filter((el): el is HTMLElement => el !== null);
    if (observed.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActive(visible[0]!.target.id);
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 },
    );
    observed.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <nav
      aria-label="Разделы настроек"
      className="lg:w-48 lg:shrink-0 lg:sticky lg:top-20 self-start"
    >
      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
        Настройки
      </div>
      <ul className="flex lg:flex-col gap-1 text-sm">
        {SECTION_ITEMS.map((s) => {
          const isActive = active === s.id;
          return (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                className={
                  "block rounded-md px-3 py-1.5 transition-colors " +
                  (isActive
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/60")
                }
                aria-current={isActive ? "true" : undefined}
              >
                {s.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

type ParseResult =
  | { kind: "ok"; value: SettingValue }
  | { kind: "empty" }
  | { kind: "error"; message: string };

function parseJson(raw: string): ParseResult {
  if (raw.trim() === "") return { kind: "empty" };
  try {
    return { kind: "ok", value: JSON.parse(raw) as SettingValue };
  } catch (err) {
    const message = err instanceof Error ? err.message : "не удалось распарсить";
    return { kind: "error", message };
  }
}

function extractMessage(err: unknown): string {
  return humanizeApiError(err);
}

// Разбираем сохранённые пороги по одному полю и добираем недостающие из
// дефолтов. All-or-nothing проверка здесь была ловушкой: стоит добавить в
// AnalyticsThresholds новый ключ — и админ, у которого в settings лежит
// объект прошлой версии, молча увидел бы все значения сброшенными к
// дефолтам вместо своих.
function parseThresholds(value: unknown): AnalyticsThresholds {
  if (typeof value !== "object" || value === null) {
    return ANALYTICS_THRESHOLDS_DEFAULTS;
  }
  const raw = value as Record<string, unknown>;
  const out = { ...ANALYTICS_THRESHOLDS_DEFAULTS };
  for (const field of THRESHOLD_FIELDS) {
    const v = raw[field.key];
    if (typeof v === "number" && Number.isFinite(v)) out[field.key] = v;
  }
  return out;
}

function isViewerCta(value: unknown): value is ViewerCta {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    (v.kind === "phone" || v.kind === "url") && typeof v.value === "string"
  );
}

// --- Карточка «Аналитика — пороги» (PRD §7.4) ---

interface ThresholdField {
  key: keyof AnalyticsThresholds;
  label: string;
  description: string;
  min: number;
  max: number;
}

// Подсказка дефолта в плейсхолдере — берём из ANALYTICS_THRESHOLDS_DEFAULTS,
// чтобы при пустом поле было видно «нормальное» значение.
function defaultHint(key: keyof AnalyticsThresholds): string {
  return `по умолчанию ${ANALYTICS_THRESHOLDS_DEFAULTS[key]}`;
}

// Диапазоны зеркалят backend AnalyticsThresholds (Field(ge=…, le=…)).
const THRESHOLD_FIELDS: ThresholdField[] = [
  {
    key: "notification_threshold_pages",
    label: "Порог «горячего лида», страниц",
    description:
      "Когда получатель пролистает столько страниц КП (и проведёт на каждой хотя бы N активных секунд — см. поле ниже), сделка автоматически становится «горячей» и менеджер получает задачу в Bitrix24.",
    min: 1,
    max: 100,
  },
  {
    key: "counted_page_min_active_seconds",
    label: "Страница засчитана: минимум активных секунд",
    description:
      "Страница идёт в счёт «горячего лида» только если получатель провёл на ней дольше этого времени. «Активные секунды» — это секунды, когда у получателя двигается мышь, идёт скролл или нажимаются клавиши (страница реально перед глазами, а не оставлена в фоне).",
    min: 1,
    max: 600,
  },
  {
    key: "counted_page_min_visible_percent",
    label: "Страница засчитана: минимум видимой части страницы, %",
    description:
      "Какая часть страницы по высоте должна попасть на экран, чтобы секунды на ней шли в счёт. Без этого порога полоска соседней страницы у края экрана набирала активное время наравне с той, которую действительно читают. Страница, которой на экране видно больше, чем остальных, засчитывается всегда — на увеличении она выше экрана и целиком в него не помещается, но читают именно её.",
    min: 1,
    max: 100,
  },
  {
    key: "notification_delay_min_seconds",
    label: "Bitrix-задержка: минимум, сек",
    description:
      "Чтобы менеджеры не получали все задачи в Bitrix24 одной пачкой, система ставит их с небольшой случайной задержкой. Это нижняя граница окна. По умолчанию — 60 секунд.",
    min: 0,
    max: 3600,
  },
  {
    key: "notification_delay_max_seconds",
    label: "Bitrix-задержка: максимум, сек",
    description:
      "Верхняя граница того же случайного окна. Должна быть ≥ минимума. По умолчанию — 180 секунд (3 минуты).",
    min: 0,
    max: 3600,
  },
  {
    key: "inactivity_timeout_seconds",
    label: "Таймаут неактивности получателя, сек",
    description:
      "Если получатель ничего не делает на странице с КП дольше этого времени (не двигает мышь, не скроллит, не нажимает клавиши) — счётчик активного времени ставится на паузу. Когда активность возобновится — счётчик пойдёт дальше.",
    min: 5,
    max: 600,
  },
];

interface ThresholdsCardProps {
  stored: AnalyticsThresholds;
  onSaved: (next: AnalyticsThresholds) => void;
}

function ThresholdsCard({ stored, onSaved }: ThresholdsCardProps) {
  // Храним строки, чтобы пользователь мог временно ввести «», а валидация
  // сама подсветила. Парсим только при сабмите.
  const [draft, setDraft] = useState<Record<keyof AnalyticsThresholds, string>>(
    () => stringifyThresholds(stored),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // При перечитывании stored из API (после сохранения) — подтягиваем в draft.
  useEffect(() => {
    setDraft(stringifyThresholds(stored));
  }, [stored]);

  const validation = useMemo(() => validateThresholdsDraft(draft), [draft]);
  const isDirty = useMemo(() => {
    return THRESHOLD_FIELDS.some(
      (f) => Number(draft[f.key]) !== stored[f.key],
    );
  }, [draft, stored]);

  async function onSave() {
    if (validation.kind !== "ok") return;
    setSaving(true);
    setError(null);
    try {
      // AnalyticsThresholds — типизированный сабсет SettingValue;
      // приводим, чтобы попасть в общий контракт PATCH.
      const res = await settingsApi.patch({
        updates: { [THRESHOLDS_KEY]: { ...validation.value } as SettingValue },
      });
      const stored = res.settings[THRESHOLDS_KEY];
      if (stored === undefined) {
        // Ответ без ключа — сохранять нечего показывать: молча подставить
        // дефолты значило бы стереть только что введённые значения и при
        // этом отрапортовать «Сохранено».
        setError("Сервер не вернул сохранённые пороги. Обновите страницу.");
        return;
      }
      onSaved(parseThresholds(stored));
      setSavedAt(new Date());
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function onRevert() {
    setDraft(stringifyThresholds(stored));
    setError(null);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Аналитика — пороги</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Когда сделка автоматически становится «горячим лидом», как
          считается активное время получателя на странице с КП и через
          сколько появится задача в Bitrix24. Изменения применяются
          сразу — перезапускать сервисы не нужно.
        </p>
        <p className="text-xs text-muted-foreground">
          Значения по умолчанию: <strong>4</strong> страницы по{" "}
          <strong>5+</strong> активных секунд каждая при попадании на экран
          от <strong>25%</strong> её высоты, задержка перед задачей в
          Bitrix24 —{" "}
          <strong>60–180</strong> секунд, пауза счётчика после{" "}
          <strong>30</strong> секунд бездействия.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          {THRESHOLD_FIELDS.map((field) => {
            const fieldError = validation.kind === "errors" ? validation.errors[field.key] : null;
            const inputId = `thresholds-${field.key}`;
            return (
              <div key={field.key} className="space-y-1">
                <Label htmlFor={inputId}>{field.label}</Label>
                <Input
                  id={inputId}
                  type="number"
                  inputMode="numeric"
                  min={field.min}
                  max={field.max}
                  placeholder={defaultHint(field.key)}
                  value={draft[field.key]}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, [field.key]: e.target.value }))
                  }
                  aria-invalid={fieldError !== null}
                  aria-describedby={`${inputId}-help`}
                />
                <p
                  id={`${inputId}-help`}
                  className={
                    fieldError
                      ? "text-xs text-destructive"
                      : "text-xs text-muted-foreground"
                  }
                >
                  {fieldError ?? field.description}
                </p>
              </div>
            );
          })}
        </div>

        {validation.kind === "errors" && validation.crossField !== null && (
          <p className="text-sm text-destructive">{validation.crossField}</p>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex items-center gap-2">
          <Button
            onClick={() => void onSave()}
            disabled={saving || validation.kind !== "ok" || !isDirty}
          >
            {saving ? "Загрузка…" : "Сохранить"}
          </Button>
          <Button
            variant="outline"
            onClick={onRevert}
            disabled={saving || !isDirty}
          >
            Отменить
          </Button>
          {savedAt && !isDirty && !error && (
            <span className="text-xs text-muted-foreground">
              Сохранено в {savedAt.toLocaleTimeString("ru-RU")}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function stringifyThresholds(
  t: AnalyticsThresholds,
): Record<keyof AnalyticsThresholds, string> {
  return {
    notification_threshold_pages: String(t.notification_threshold_pages),
    counted_page_min_active_seconds: String(t.counted_page_min_active_seconds),
    counted_page_min_visible_percent: String(t.counted_page_min_visible_percent),
    notification_delay_min_seconds: String(t.notification_delay_min_seconds),
    notification_delay_max_seconds: String(t.notification_delay_max_seconds),
    inactivity_timeout_seconds: String(t.inactivity_timeout_seconds),
  };
}

type ThresholdsValidation =
  | { kind: "ok"; value: AnalyticsThresholds }
  | {
      kind: "errors";
      errors: Partial<Record<keyof AnalyticsThresholds, string>>;
      crossField: string | null;
    };

function validateThresholdsDraft(
  draft: Record<keyof AnalyticsThresholds, string>,
): ThresholdsValidation {
  const errors: Partial<Record<keyof AnalyticsThresholds, string>> = {};
  const parsed: Partial<AnalyticsThresholds> = {};
  for (const field of THRESHOLD_FIELDS) {
    const raw = draft[field.key].trim();
    if (raw === "") {
      errors[field.key] = "Заполните поле";
      continue;
    }
    const n = Number(raw);
    if (!Number.isInteger(n)) {
      errors[field.key] = "Введите целое число";
      continue;
    }
    if (n < field.min || n > field.max) {
      errors[field.key] = `Допустимо от ${field.min} до ${field.max}`;
      continue;
    }
    parsed[field.key] = n;
  }

  let crossField: string | null = null;
  if (
    parsed.notification_delay_min_seconds !== undefined &&
    parsed.notification_delay_max_seconds !== undefined &&
    parsed.notification_delay_min_seconds > parsed.notification_delay_max_seconds
  ) {
    crossField =
      "Минимум Bitrix-задержки не может быть больше максимума.";
  }

  if (Object.keys(errors).length > 0 || crossField !== null) {
    return { kind: "errors", errors, crossField };
  }
  return { kind: "ok", value: parsed as AnalyticsThresholds };
}

// --- Карточка «CTA-кнопка в viewer» (PRD §6.2) ---

// Зеркалит backend ViewerCta-валидаторы (schemas/public_viewer.py).
const PHONE_RE = /^\+[0-9]{6,20}$/;
const URL_RE = /^https?:\/\/[^\s]{4,2048}$/;

type CtaDraftMode = "disabled" | CtaKind;

interface CtaCardProps {
  stored: ViewerCta | null;
  onSaved: (next: ViewerCta | null) => void;
}

function CtaCard({ stored, onSaved }: CtaCardProps) {
  const [mode, setMode] = useState<CtaDraftMode>(stored?.kind ?? "disabled");
  const [phone, setPhone] = useState<string>(
    stored?.kind === "phone" ? stored.value : "",
  );
  const [url, setUrl] = useState<string>(
    stored?.kind === "url" ? stored.value : "",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    setMode(stored?.kind ?? "disabled");
    setPhone(stored?.kind === "phone" ? stored.value : "");
    setUrl(stored?.kind === "url" ? stored.value : "");
  }, [stored]);

  const validation = useMemo<
    | { kind: "ok"; value: ViewerCta | null }
    | { kind: "error"; field: "phone" | "url"; message: string }
  >(() => {
    if (mode === "disabled") return { kind: "ok", value: null };
    if (mode === "phone") {
      const value = phone.trim();
      if (!PHONE_RE.test(value)) {
        return {
          kind: "error",
          field: "phone",
          message:
            "Номер в международном формате: «+» и 6–20 цифр без пробелов, тире и скобок. Например, +71234567890.",
        };
      }
      return { kind: "ok", value: { kind: "phone", value } };
    }
    const value = url.trim();
    if (!URL_RE.test(value)) {
      return {
        kind: "error",
        field: "url",
        message: "URL должен начинаться с http:// или https://",
      };
    }
    return { kind: "ok", value: { kind: "url", value } };
  }, [mode, phone, url]);

  const isDirty = useMemo(() => {
    if (validation.kind !== "ok") return true;
    const next = validation.value;
    if (next === null && stored === null) return false;
    if (next === null || stored === null) return true;
    return next.kind !== stored.kind || next.value !== stored.value;
  }, [validation, stored]);

  async function onSave() {
    if (validation.kind !== "ok") return;
    setSaving(true);
    setError(null);
    try {
      const payload: SettingValue =
        validation.value === null
          ? null
          : ({ ...validation.value } as SettingValue);
      const res = await settingsApi.patch({ updates: { [CTA_KEY]: payload } });
      const stored = res.settings[CTA_KEY];
      const next = isViewerCta(stored) ? stored : null;
      onSaved(next);
      setSavedAt(new Date());
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function onRevert() {
    setMode(stored?.kind ?? "disabled");
    setPhone(stored?.kind === "phone" ? stored.value : "");
    setUrl(stored?.kind === "url" ? stored.value : "");
    setError(null);
  }

  const phoneError =
    validation.kind === "error" && validation.field === "phone"
      ? validation.message
      : null;
  const urlError =
    validation.kind === "error" && validation.field === "url"
      ? validation.message
      : null;

  return (
    <Card data-testid="cta-card">
      <CardHeader>
        <CardTitle>Кнопка действия на странице с КП</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Большая зелёная кнопка «Связаться с нами» в правом нижнем углу
          страницы с КП у получателя. По умолчанию ведёт на общий номер
          TraceLink — можно переопределить своим телефоном или ссылкой
          (например, на форму обратной связи). Каждый клик попадает
          в статистику документа — будет видно, кто нажал.
        </p>

        <div className="space-y-2">
          <Label>Куда ведёт кнопка</Label>
          <div className="flex flex-wrap gap-2">
            {(["disabled", "phone", "url"] as CtaDraftMode[]).map((m) => (
              <Button
                key={m}
                type="button"
                size="sm"
                variant={mode === m ? "default" : "outline"}
                aria-pressed={mode === m}
                onClick={() => setMode(m)}
              >
                {m === "disabled"
                  ? "По умолчанию"
                  : m === "phone"
                  ? "Свой телефон"
                  : "Своя ссылка"}
              </Button>
            ))}
          </div>
        </div>

        {mode === "phone" && (
          <div className="space-y-1">
            <Label htmlFor="cta-phone">Номер телефона</Label>
            <Input
              id="cta-phone"
              type="tel"
              inputMode="tel"
              placeholder="+71234567890"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              aria-invalid={phoneError !== null}
              aria-describedby="cta-phone-help"
            />
            <p
              id="cta-phone-help"
              className={
                phoneError
                  ? "text-xs text-destructive"
                  : "text-xs text-muted-foreground"
              }
            >
              {phoneError ??
                "В международном формате с «+», без пробелов, тире и скобок. Например, +71234567890."}
            </p>
          </div>
        )}

        {mode === "url" && (
          <div className="space-y-1">
            <Label htmlFor="cta-url">URL</Label>
            <Input
              id="cta-url"
              type="url"
              inputMode="url"
              placeholder="https://example.com/contact"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              aria-invalid={urlError !== null}
              aria-describedby="cta-url-help"
            />
            <p
              id="cta-url-help"
              className={
                urlError
                  ? "text-xs text-destructive"
                  : "text-xs text-muted-foreground"
              }
            >
              {urlError ?? "Откроется у получателя в новой вкладке."}
            </p>
          </div>
        )}

        <CtaPreview
          mode={mode}
          isPhoneValid={mode === "phone" && PHONE_RE.test(phone.trim())}
          isUrlValid={mode === "url" && URL_RE.test(url.trim())}
        />

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex items-center gap-2">
          <Button
            onClick={() => void onSave()}
            disabled={saving || validation.kind !== "ok" || !isDirty}
          >
            {saving ? "Загрузка…" : "Сохранить"}
          </Button>
          <Button
            variant="outline"
            onClick={onRevert}
            disabled={saving || !isDirty}
          >
            Отменить
          </Button>
          {savedAt && !isDirty && !error && (
            <span className="text-xs text-muted-foreground">
              Сохранено в {savedAt.toLocaleTimeString("ru-RU")}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Превью кнопки: показывает, как viewer отрисует её у получателя.
// Стили зеркалят viewer/src/components/FloatingCta.tsx — если меняется
// одно, второе тоже стоит обновить.
// Зеркалит DEFAULT_VIEWER_CTA в backend/src/tracelink/services/viewer_cta.py
// — если меняется один, второе тоже обновить.
const DEFAULT_TRACELINK_PHONE = "+79278501745";

function CtaPreview({
  mode,
  isPhoneValid,
  isUrlValid,
}: {
  mode: CtaDraftMode;
  isPhoneValid: boolean;
  isUrlValid: boolean;
}) {
  const isValid =
    mode === "disabled" ||
    (mode === "phone" && isPhoneValid) ||
    (mode === "url" && isUrlValid);
  const hint =
    mode === "disabled"
      ? `Так это увидит получатель — кнопка ведёт на общий номер TraceLink (${DEFAULT_TRACELINK_PHONE}):`
      : "Так это увидит получатель в правом нижнем углу страницы с КП:";
  return (
    <div className="rounded-md border bg-muted/30 p-4">
      <p className="text-xs text-muted-foreground mb-3">{hint}</p>
      <div className="flex justify-end">
        <span
          className={
            "inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium shadow-lg " +
            (isValid
              ? "bg-emerald-600 text-white"
              : "bg-muted text-muted-foreground")
          }
        >
          Связаться с нами
        </span>
      </div>
    </div>
  );
}

// --- Карточка «URL политики конфиденциальности» (PRD §13.3) ---

interface PrivacyUrlCardProps {
  stored: string | null;
  onSaved: (next: string | null) => void;
}

// Зеркалит backend PrivacyUrlSetting (schemas/settings.py): принимаем
// абсолютный путь /... или http(s)-URL; пустая строка → null (дефолт).
function PrivacyUrlCard({ stored, onSaved }: PrivacyUrlCardProps) {
  const [draft, setDraft] = useState<string>(stored ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    setDraft(stored ?? "");
  }, [stored]);

  const validation = useMemo<
    { kind: "ok"; value: string | null } | { kind: "error"; message: string }
  >(() => {
    const value = draft.trim();
    if (value === "") return { kind: "ok", value: null };
    if (value.length > 2048) {
      return { kind: "error", message: "URL не длиннее 2048 символов" };
    }
    if (
      !value.startsWith("/") &&
      !value.startsWith("http://") &&
      !value.startsWith("https://")
    ) {
      return {
        kind: "error",
        message:
          "URL должен начинаться с http:// или https://, либо со слэша (/…) если это путь на этом же сайте.",
      };
    }
    return { kind: "ok", value };
  }, [draft]);

  const isDirty = useMemo(() => {
    if (validation.kind !== "ok") return true;
    return (validation.value ?? "") !== (stored ?? "");
  }, [validation, stored]);

  async function onSave() {
    if (validation.kind !== "ok") return;
    setSaving(true);
    setError(null);
    try {
      const payload: SettingValue = validation.value;
      const res = await settingsApi.patch({
        updates: { [PRIVACY_URL_KEY]: payload },
      });
      const next = res.settings[PRIVACY_URL_KEY];
      const normalized =
        typeof next === "string" && next.length > 0 ? next : null;
      onSaved(normalized);
      setSavedAt(new Date());
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function onRevert() {
    setDraft(stored ?? "");
    setError(null);
  }

  const inputError = validation.kind === "error" ? validation.message : null;

  return (
    <Card data-testid="privacy-url-card">
      <CardHeader>
        <CardTitle>Политика конфиденциальности</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Внизу страницы с КП у получателя есть маленькая ссылка «Политика
          конфиденциальности» — этого требует 152-ФЗ, потому что мы фиксируем
          действия получателя (открытие, время на страницах, клики).
        </p>
        <p className="text-sm text-muted-foreground">
          Сюда можно вписать ссылку на политику вашей компании. Если оставить
          пусто — получатель увидит дефолтную страницу с политикой на этом же
          домене.
        </p>

        <div className="space-y-1">
          <Label htmlFor="privacy-url">URL</Label>
          <Input
            id="privacy-url"
            type="text"
            inputMode="url"
            placeholder="/privacy"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-invalid={inputError !== null}
            aria-describedby="privacy-url-help"
          />
          <p
            id="privacy-url-help"
            className={
              inputError
                ? "text-xs text-destructive"
                : "text-xs text-muted-foreground"
            }
          >
            {inputError ??
              "Можно указать ссылку на политику вашей компании (https://…) или путь на этом же сайте (/privacy). Пусто — будет показана дефолтная страница."}
          </p>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex items-center gap-2">
          <Button
            onClick={() => void onSave()}
            disabled={saving || validation.kind !== "ok" || !isDirty}
          >
            {saving ? "Загрузка…" : "Сохранить"}
          </Button>
          <Button
            variant="outline"
            onClick={onRevert}
            disabled={saving || !isDirty}
          >
            Отменить
          </Button>
          {savedAt && !isDirty && !error && (
            <span className="text-xs text-muted-foreground">
              Сохранено в {savedAt.toLocaleTimeString("ru-RU")}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
